import { FC, useEffect, useRef } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Application } from 'models'
import { useAppDispatch } from 'store/hooks'
import { closeToast, openToast, ToastVariant } from 'store/toasts/toastSlice'
import { ApplicationsServiceV4 } from 'services/v4/applications-v4.service'
import { connectionErrorMessage } from './applicationProviders'
import { APPLICATIONS_PATH, CONNECT_FAILED_MESSAGE, freshStartPath } from './applicationSetup.constants'

const INVALID_STATE = 'invalid_state'

const isInvalidState = (e: unknown): boolean =>
  (e as any)?.response?.status === 400 && (e as any)?.response?.data?.detail === INVALID_STATE

const backendMessage = (e: unknown): string => (e as any)?.response?.data?.message || ''

/* The landing page reads the outcome from its query, exactly as it did when the API redirected. */
const landingPath = (application: Application): string => {
  const query = new URLSearchParams({ application_id: application.id })
  const { connection } = application

  if (connection?.status === 'FAILED' && connection.error_code) query.set('error', connection.error_code)

  return `${freshStartPath(application.manifest?.slug ?? '')}?${query}`
}

/* Where the provider sends the browser back after the approval. It hands the query to the
 * signed-in completion and replaces itself, so the code never stays in the history. */
const ApplicationCallback: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const completing = useRef(false)

  useEffect(() => {
    if (completing.current) return
    completing.current = true

    /* Not cleared on unmount: the toast outlives this page, which is gone before it closes. */
    const leaveForList = (text: string) => {
      dispatch(openToast({ variant: ToastVariant.error, props: { text, description: '' } }))
      setTimeout(() => dispatch(closeToast()), 5000)
      navigate(APPLICATIONS_PATH, { replace: true })
    }

    const state = searchParams.get('state')
    const refused = connectionErrorMessage(undefined, INVALID_STATE)

    if (!state) {
      leaveForList(refused)
      return
    }

    ApplicationsServiceV4.completeApplication({
      state,
      code: searchParams.get('code'),
      error: searchParams.get('error')
    })
      .then((application) => {
        if (!application?.manifest?.slug) throw new Error('The completion answered no manifest')

        /* Handed over so the setup page opens on the step this approval reached, with no
           first step shown while it fetches the same instance again. */
        navigate(landingPath(application), { replace: true, state: { application } })
      })
      .catch((e) => {
        console.error(e)
        leaveForList(isInvalidState(e) ? refused : backendMessage(e) || CONNECT_FAILED_MESSAGE)
      })
  }, [searchParams, navigate, dispatch])

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      <CircularProgress />
    </Box>
  )
}

export default ApplicationCallback
